/**
 * Oturum kaydı (session replay) redaksiyon kütüphanesi.
 *
 * AYNI dosya cityapi `src/replay/redaction.ts` içinde de vardır
 * (istemci 1. geçiş, sunucu 2. geçiş). Mantık değişirse iki kopya birlikte
 * güncellenir; `redaction.spec.ts` fixture'ları sözleşmedir.
 *
 * Hedef: token/şifre/anahtar/kart gibi sırların kayda GİRMEMESİ. Müşteri
 * bilgisi (ad, telefon, e-posta) bilinçli olarak maskelenmez (karar K3).
 * Bağımlılık yok; tarayıcı ve Node'da aynı çalışır.
 */

export const REDACTED = '[REDACTED]';

/** İstek/yanıt gövdesi üst sınırları (UTF-8 bayt). */
export const MAX_REQUEST_BODY_BYTES = 32 * 1024;
export const MAX_RESPONSE_BODY_BYTES = 64 * 1024;
/** Konsol satırı üst sınırı (karakter). */
export const MAX_CONSOLE_CHARS = 2000;

/** Görünmesine izin verilen HTTP başlıkları (küçük harf). */
const HEADER_ALLOWLIST = new Set(['content-type', 'accept', 'content-length', 'x-request-id', 'x-response-time', 'cache-control']);
/** Değeri her zaman [REDACTED] olarak gösterilen başlıklar. */
const HEADER_SENSITIVE = new Set(['authorization', 'cookie', 'set-cookie', 'x-api-key', 'x-auth-token', 'proxy-authorization']);

const SENSITIVE_KEY_RE =
  /^(authorization|token|accesstoken|refreshtoken|access_token|refresh_token|id_token|idtoken|password|passwd|pwd|newpassword|new_password|oldpassword|old_password|currentpassword|current_password|secret|clientsecret|client_secret|apikey|api_key|x-api-key|otp|otpcode|otp_code|cvv|cvc|pan|cardnumber|card_number|iban|pushtoken|push_token|signature|privatekey|private_key|sessionid|session_id|cookie|set-cookie|cloudflaretoken|cloudflare_token|turnstiletoken|uploadtoken|upload_token|secretkey|secret_key)$/i;

/** `code` alanı yalnız 4–8 haneli sayısal ise (OTP/doğrulama kodu) gizlenir. */
const NUMERIC_CODE_RE = /^\d{4,8}$/;

const JWT_RE = /\beyJ[\w-]{10,}\.[\w-]{10,}\.[\w-]{10,}\b/g;
const BEARER_RE = /\bBearer\s+[\w\-.~+/=]{8,}/gi;
/** 13–19 hane, boşluk/tire ayraçlı olabilir; Luhn doğrulamasıyla teyit edilir. */
const CARD_CANDIDATE_RE = /\b(?:\d[ -]?){12,18}\d\b/g;
const URL_SENSITIVE_PARAM_RE =
  /([?&](?:token|access_token|refresh_token|id_token|signature|x-amz-signature|x-amz-credential|x-amz-security-token|x-goog-signature|x-goog-credential|key|apikey|api_key|secret|password|otp)=)[^&#\s]*/gi;
/** Yol içindeki uzun hex token'lar (kayıp belge yükleme linki gibi): son 4 karakter kalır. */
const LONG_HEX_RE = /\b([a-f0-9]{44,})([a-f0-9]{4})\b/gi;

function luhnValid(digits: string): boolean {
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = digits.charCodeAt(i) - 48;
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

/** Metin içindeki sırları gizler (JWT, Bearer, kart numarası, URL parametreleri). */
export function redactText(input: string): string {
  if (!input) return input;
  let out = input.replace(JWT_RE, REDACTED).replace(BEARER_RE, `Bearer ${REDACTED}`);
  out = out.replace(URL_SENSITIVE_PARAM_RE, `$1${REDACTED}`);
  out = out.replace(LONG_HEX_RE, (_m, _head: string, tail: string) => `${REDACTED}${tail}`);
  out = out.replace(CARD_CANDIDATE_RE, (m) => {
    const digits = m.replace(/[ -]/g, '');
    if (digits.length < 13 || digits.length > 19 || !luhnValid(digits)) return m;
    return `**** **** **** ${digits.slice(-4)}`;
  });
  return out;
}

/** URL'yi sırlardan arındırır (query token'ları, uzun hex yol parçaları). */
export function redactUrl(url: string): string {
  return redactText(url);
}

/** Başlıkları allowlist'e indirger; hassas olanları [REDACTED] ile işaretler. */
export function redactHeaders(headers: Record<string, unknown> | undefined | null): Record<string, string> {
  const out: Record<string, string> = {};
  if (!headers) return out;
  for (const [rawKey, value] of Object.entries(headers)) {
    const key = rawKey.toLowerCase();
    if (HEADER_SENSITIVE.has(key)) {
      out[key] = REDACTED;
    } else if (HEADER_ALLOWLIST.has(key)) {
      out[key] = redactText(String(value));
    }
    // diğer başlıklar düşer
  }
  return out;
}

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY_RE.test(key.trim());
}

/** JSON değerini derin gezerek anahtar ve değer bazlı redaksiyon uygular. */
export function redactValue(value: unknown, key?: string, depth = 0): unknown {
  if (depth > 32) return '[TOO_DEEP]';
  if (key !== undefined && isSensitiveKey(key)) return REDACTED;
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    if (key !== undefined && /^code$/i.test(key) && NUMERIC_CODE_RE.test(value)) return REDACTED;
    return redactText(value);
  }
  if (typeof value === 'number') {
    if (key !== undefined && /^code$/i.test(key) && NUMERIC_CODE_RE.test(String(value))) return REDACTED;
    return value;
  }
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.map((v) => redactValue(v, undefined, depth + 1));
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = redactValue(v, k, depth + 1);
    }
    return out;
  }
  return String(value);
}

function utf8Bytes(s: string): number {
  // Node: Buffer varsa kesin; tarayıcı: TextEncoder
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s).length;
  return s.length;
}

/** Metni bayt sınırına kırpar (çok baytlı karakteri ortadan bölmez). */
export function truncateBytes(text: string, maxBytes: number): { text: string; truncated: boolean; bytes: number } {
  const bytes = utf8Bytes(text);
  if (bytes <= maxBytes) return { text, truncated: false, bytes };
  // Yaklaşık kes, sonra sınırın altına in
  let cut = text.slice(0, maxBytes);
  while (utf8Bytes(cut) > maxBytes) cut = cut.slice(0, cut.length - Math.ceil(cut.length * 0.05) - 1);
  return { text: cut, truncated: true, bytes };
}

export interface RedactedBody {
  /** Redakte + kırpılmış gövde (JSON ise yeniden string'lenmiş). */
  text: string | null;
  truncated: boolean;
  bytes: number;
  json: boolean;
}

/** Gövde metnini (JSON veya düz) redakte eder ve bayt sınırına kırpar. */
export function redactBody(body: string | null | undefined, maxBytes: number): RedactedBody {
  if (body === null || body === undefined || body === '') {
    return { text: null, truncated: false, bytes: 0, json: false };
  }
  let json = false;
  let redacted: string;
  const trimmed = body.trimStart();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      redacted = JSON.stringify(redactValue(JSON.parse(body)));
      json = true;
    } catch {
      redacted = redactText(body);
    }
  } else {
    redacted = redactText(body);
  }
  const t = truncateBytes(redacted, maxBytes);
  return { text: t.text, truncated: t.truncated, bytes: t.bytes, json };
}

/** Kayıttaki `network` özel olayının yükü. */
export interface NetworkEventPayload {
  id: string;
  method: string;
  url: string;
  status: number;
  ok: boolean;
  durationMs: number;
  requestId?: string;
  reqHeaders?: Record<string, string>;
  reqBody?: string | null;
  reqTruncated?: boolean;
  resHeaders?: Record<string, string>;
  resBody?: string | null;
  resTruncated?: boolean;
  resType?: string;
  error?: string;
  /** İsteğin gerçek zamanı (ms) — kayıt başlamadan önce kuyruklanan istekler için */
  capturedAt?: number;
  /** Kayıt başlamadan önce atılmış, başlarken eklenmiş */
  preStart?: boolean;
  /** Özet mod: gövdeler bilinçli olarak kaydedilmedi (karar 20 Eyl 2026) */
  bodiesOmitted?: boolean;
}

export function redactNetworkEvent(ev: NetworkEventPayload): NetworkEventPayload {
  const req = redactBody(ev.reqBody, MAX_REQUEST_BODY_BYTES);
  const res = redactBody(ev.resBody, MAX_RESPONSE_BODY_BYTES);
  return {
    ...ev,
    url: redactUrl(ev.url),
    reqHeaders: redactHeaders(ev.reqHeaders),
    resHeaders: redactHeaders(ev.resHeaders),
    reqBody: req.text,
    reqTruncated: ev.reqTruncated || req.truncated,
    resBody: res.text,
    resTruncated: ev.resTruncated || res.truncated,
    error: ev.error ? redactText(ev.error) : ev.error
  };
}

/* ---------------- rrweb olay ağacı ---------------- */

// rrweb EventType / IncrementalSource sayısal sabitleri (paket bağımlılığı olmasın)
const EVENT_FULL_SNAPSHOT = 2;
const EVENT_INCREMENTAL = 3;
const EVENT_CUSTOM = 5;
const EVENT_PLUGIN = 6;
const SOURCE_MUTATION = 0;
const SOURCE_INPUT = 5;

type AnyRecord = Record<string, unknown>;

function redactDomNode(node: unknown, depth = 0): void {
  if (!node || typeof node !== 'object' || depth > 200) return;
  const n = node as AnyRecord;
  if (typeof n.textContent === 'string') n.textContent = redactText(n.textContent);
  if (n.attributes && typeof n.attributes === 'object') {
    const attrs = n.attributes as AnyRecord;
    for (const [k, v] of Object.entries(attrs)) {
      if (typeof v === 'string') attrs[k] = redactText(v);
    }
  }
  if (Array.isArray(n.childNodes)) {
    for (const child of n.childNodes) redactDomNode(child, depth + 1);
  }
}

/**
 * rrweb olay dizisini yerinde (in place) redakte eder ve aynı diziyi döner.
 * - Custom `network` olayları: başlık/gövde/URL redaksiyonu
 * - Plugin `rrweb/console@1`: konsol satırları metin redaksiyonu + uzunluk sınırı
 * - Input olayları: girilen metin (JWT/kart gibi) redaksiyonu
 * - Tam snapshot + mutation: metin düğümleri ve attribute değerleri
 */
export function redactRrwebEvents<T>(events: T[]): T[] {
  for (const raw of events) {
    if (!raw || typeof raw !== 'object') continue;
    const ev = raw as AnyRecord;
    const data = ev.data as AnyRecord | undefined;
    switch (ev.type) {
      case EVENT_CUSTOM: {
        if (!data) break;
        if (data.tag === 'network' && data.payload && typeof data.payload === 'object') {
          data.payload = redactNetworkEvent(data.payload as NetworkEventPayload);
        } else if (data.payload !== undefined) {
          data.payload = redactValue(data.payload);
        }
        break;
      }
      case EVENT_PLUGIN: {
        if (!data || data.plugin !== 'rrweb/console@1') break;
        const payload = data.payload as AnyRecord | undefined;
        if (payload && Array.isArray(payload.payload)) {
          payload.payload = payload.payload.map((line: unknown) => {
            const s = typeof line === 'string' ? line : JSON.stringify(line);
            const r = redactText(s ?? '');
            return r.length > MAX_CONSOLE_CHARS ? `${r.slice(0, MAX_CONSOLE_CHARS)}…[kırpıldı]` : r;
          });
        }
        if (payload && typeof payload.trace !== 'undefined' && Array.isArray(payload.trace)) {
          payload.trace = payload.trace.map((t: unknown) => (typeof t === 'string' ? redactText(t) : t));
        }
        break;
      }
      case EVENT_INCREMENTAL: {
        if (!data) break;
        if (data.source === SOURCE_INPUT && typeof data.text === 'string') {
          data.text = redactText(data.text);
        } else if (data.source === SOURCE_MUTATION) {
          if (Array.isArray(data.texts)) {
            for (const t of data.texts as AnyRecord[]) {
              if (typeof t.value === 'string') t.value = redactText(t.value);
            }
          }
          if (Array.isArray(data.attributes)) {
            for (const a of data.attributes as AnyRecord[]) {
              const attrs = a.attributes as AnyRecord | undefined;
              if (!attrs) continue;
              for (const [k, v] of Object.entries(attrs)) {
                if (typeof v === 'string') attrs[k] = redactText(v);
              }
            }
          }
          if (Array.isArray(data.adds)) {
            for (const add of data.adds as AnyRecord[]) redactDomNode(add.node);
          }
        }
        break;
      }
      case EVENT_FULL_SNAPSHOT: {
        if (data) redactDomNode(data.node);
        break;
      }
      default:
        break;
    }
  }
  return events;
}

/** Bir olay dizisinden özet çıkarır (Mongo chunk metadata'sı için). */
export interface ChunkSummary {
  eventCount: number;
  hasFullSnapshot: boolean;
  errorCount: number;
  network: Array<{
    id: string;
    method: string;
    url: string;
    status: number;
    durationMs: number;
    ts: number;
  }>;
  urls: string[];
  fromTs: number | null;
  toTs: number | null;
}

export function summarizeRrwebEvents(events: unknown[]): ChunkSummary {
  const summary: ChunkSummary = {
    eventCount: events.length,
    hasFullSnapshot: false,
    errorCount: 0,
    network: [],
    urls: [],
    fromTs: null,
    toTs: null
  };
  for (const raw of events) {
    if (!raw || typeof raw !== 'object') continue;
    const ev = raw as AnyRecord;
    const ts = typeof ev.timestamp === 'number' ? ev.timestamp : null;
    if (ts !== null) {
      if (summary.fromTs === null || ts < summary.fromTs) summary.fromTs = ts;
      if (summary.toTs === null || ts > summary.toTs) summary.toTs = ts;
    }
    if (ev.type === EVENT_FULL_SNAPSHOT) summary.hasFullSnapshot = true;
    if (ev.type === EVENT_CUSTOM) {
      const data = ev.data as AnyRecord | undefined;
      const payload = (data?.payload ?? {}) as AnyRecord;
      if (data?.tag === 'error') summary.errorCount++;
      if (data?.tag === 'network' && summary.network.length < 500) {
        summary.network.push({
          id: String(payload.id ?? ''),
          method: String(payload.method ?? 'GET'),
          url: String(payload.url ?? ''),
          status: Number(payload.status ?? 0),
          durationMs: Number(payload.durationMs ?? 0),
          ts: ts ?? 0
        });
      }
      if (data?.tag === 'nav' && typeof payload.to === 'string' && summary.urls.length < 100) {
        summary.urls.push(payload.to);
      }
    }
  }
  return summary;
}
